'use client';

import { c as _c } from "react/compiler-runtime";
import { noop } from 'lodash';
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useURLHashState } from '@src/hooks/useURLHashState';
import { delay } from '@src/utils/delay';
import { MODAL_HASH_PREFIX, isAppModalHash } from '@src/utils/hash.util';
export type SetCloseOptions = {
  /** Use replaceState instead of history.back() when clearing the hash. */
  navigation?: 'back' | 'replace';
};
export interface ModalContextType {
  isOpen: (modalId: string) => boolean;
  setClose: (modalId: string, options?: SetCloseOptions) => void;
  setOpen: (modalId: string) => void;
}
const ModalContext = createContext<ModalContextType>({
  isOpen: () => false,
  setClose: noop,
  setOpen: noop
});
interface ModalProviderProps {
  children: ReactNode;
}
export const ModalProvider = t0 => {
  const $ = _c(20);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [_isOpen, _setOpen] = useState(t1);
  const [hash, setHash] = useURLHashState();
  let t2;
  let t3;
  if ($[1] !== hash) {
    t2 = () => {
      _setOpen({});
      const isValidHash = isAppModalHash(hash);
      if (!isValidHash) {
        return;
      }
      const isAnotherModalOpen = document.querySelector("[data-headlessui-state=\"open\"]") && document.querySelector("[data-headlessui-state=\"open\"]")?.hasAttribute("data-open");
      isAnotherModalOpen ? delay(1000).then(() => _setOpen({
        [hash]: true
      })) : _setOpen({
        [hash]: true
      });
    };
    t3 = [hash];
    $[1] = hash;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== setHash) {
    t4 = (modalId, val) => {
      setHash(val ? `${MODAL_HASH_PREFIX}${modalId}` : "");
    };
    $[4] = setHash;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const set = t4;
  let t5;
  if ($[6] !== set) {
    t5 = modalId_0 => {
      set(modalId_0, true);
    };
    $[6] = set;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const setOpen = t5;
  let t6;
  if ($[8] !== set || $[9] !== setHash) {
    t6 = (modalId_1, options) => {
      if (options?.navigation === "replace") {
        _setOpen({});
        setHash("", {
          navigation: "replace"
        });
        return;
      }
      set(modalId_1, false);
    };
    $[8] = set;
    $[9] = setHash;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const setClose = t6;
  let t7;
  if ($[11] !== _isOpen) {
    t7 = modalId_2 => _isOpen?.[`${MODAL_HASH_PREFIX}${modalId_2}`] ?? _isOpen?.[modalId_2] ?? false;
    $[11] = _isOpen;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const isOpen = t7;
  let t8;
  if ($[13] !== isOpen || $[14] !== setClose || $[15] !== setOpen) {
    t8 = {
      setClose,
      setOpen,
      isOpen
    };
    $[13] = isOpen;
    $[14] = setClose;
    $[15] = setOpen;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== children || $[18] !== t8) {
    t9 = <ModalContext.Provider value={t8}>{children}</ModalContext.Provider>;
    $[17] = children;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
export const useModal = () => {
  return useContext(ModalContext);
};